/**
 * AdvancedDataSeeder.js - AI-Powered Ghost Data Generator with Pexels Integration
 *
 * Uses real photos/videos from Pexels API and a Persona Engine to create
 * realistic, themed content that matches each Ghost User's personality.
 *
 * NOTE: Uses browser-safe fetch() directly to Pexels API (not their Node SDK)
 */

import { faker } from '@faker-js/faker';

// ============================================
// API CONFIGURATION
// ============================================

/**
 * PEXELS API - Browser-safe implementation using native fetch()
 * Get API key at: https://www.pexels.com/api/
 */
const PEXELS_API_KEY = import.meta.env.VITE_PEXELS_API_KEY || null;

// Pexels API endpoints
const PEXELS_PHOTO_API = 'https://api.pexels.com/v1';
const PEXELS_VIDEO_API = 'https://api.pexels.com/videos';

// Check if Pexels is enabled (has API key)
const pexelsEnabled = !!PEXELS_API_KEY;

// Cache duration: 1 hour (in milliseconds)
const CACHE_DURATION = 60 * 60 * 1000;

// Static fallback images (Unsplash - no API needed, direct CDN links)
const FALLBACK_IMAGES = {
  avatar: [
    'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1527980965255-d3b416303d12?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&h=200&fit=crop',
    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop',
  ],
  post: [
    'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1557683316-973673baf926?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1470225620780-dba8ba36b745?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1504704911898-68304a7d2807?w=400&h=600&fit=crop',
    'https://images.unsplash.com/photo-1506157786151-b8491531f063?w=400&h=600&fit=crop',
  ]
};

// Track if we're rate limited to avoid hammering the API
let isRateLimited = false;
let rateLimitResetTime = 0;

/**
 * Get cached data from localStorage
 */
function getCached(key) {
  try {
    const cached = localStorage.getItem(`gjyl_pexels_${key}`);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (e) {
    // Ignore cache errors
  }
  return null;
}

/**
 * Set cached data in localStorage
 */
function setCache(key, data) {
  try {
    localStorage.setItem(`gjyl_pexels_${key}`, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    // Ignore cache errors (quota exceeded, etc)
  }
}

/**
 * Get random fallback image
 */
function getRandomFallback(type = 'post') {
  const images = FALLBACK_IMAGES[type] || FALLBACK_IMAGES.post;
  return images[Math.floor(Math.random() * images.length)];
}

// ============================================
// PERSONA ENGINE - THE BRAIN
// ============================================

/**
 * The 5 Personas - Each defines a unique vibe and content style
 */
const PERSONAS = [
  {
    id: 'neon_drifter',
    name: 'Neon Drifter',
    description: 'Cyberpunk aesthetic, rain-soaked streets, neon dreams',
    keywords: ['neon city', 'cyberpunk', 'rain city night', 'futuristic street', 'neon lights portrait'],
    bioKeywords: ['nocturnal', 'cyberpunk enthusiast', 'neon dreamer', 'synthetic reality', 'future vibes'],
    videoKeywords: ['cyberpunk city', 'neon street night', 'rain city', 'futuristic urban', 'night city lights'],
    captionStyle: ['Living in a neon dream ⚡', 'Rainy nights hit different 🌧️', 'Cyberpunk reality check', 'The future is now', 'Neon-soaked vibes only'],
    musicStyle: 'Synthwave Dreams',
    gradient: 'from-purple-500 via-pink-500 to-cyan-500'
  },
  {
    id: 'tech_prime',
    name: 'Tech Prime',
    description: 'Gaming, coding, tech setups, digital mastery',
    keywords: ['gaming setup', 'mechanical keyboard', 'coding screen', 'tech workspace portrait', 'gamer'],
    bioKeywords: ['developer', 'gamer', 'tech wizard', 'code ninja', 'digital architect'],
    videoKeywords: ['gaming setup', 'coding', 'tech workspace', 'computer setup', 'keyboard typing'],
    captionStyle: ['New setup reveal 💻', 'Grinding never stops 🎮', 'Code and conquer', 'Built different fr', 'Tech heaven right here'],
    musicStyle: 'Lo-Fi Beats',
    gradient: 'from-blue-500 via-indigo-500 to-purple-500'
  },
  {
    id: 'glitch_art',
    name: 'Glitch Art',
    description: 'Digital art, abstract visuals, vaporwave aesthetic',
    keywords: ['abstract art', 'digital art portrait', 'neon lights', 'glitch effect', 'vaporwave aesthetic'],
    bioKeywords: ['digital artist', 'creative soul', 'abstract mind', 'glitch enthusiast', 'vaporwave'],
    videoKeywords: ['abstract art', 'digital art', 'neon abstract', 'artistic lights', 'creative visuals'],
    captionStyle: ['Art in the digital age 🎨', 'Glitching through reality', 'Abstract expressionism', 'Creating digital dreams', 'Vaporwave forever'],
    musicStyle: 'Vaporwave Paradise',
    gradient: 'from-pink-500 via-purple-500 to-blue-500'
  },
  {
    id: 'urban_soul',
    name: 'Urban Soul',
    description: 'Street culture, fashion, urban exploration',
    keywords: ['street fashion', 'urban portrait', 'graffiti art', 'streetwear', 'city fashion'],
    bioKeywords: ['street culture', 'urban explorer', 'fashion forward', 'city dweller', 'street vibes'],
    videoKeywords: ['street fashion', 'urban', 'city street', 'graffiti', 'streetwear fashion'],
    captionStyle: ['Streets raised me 🏙️', 'Urban jungle energy', 'Fashion is my language', 'City lights, big dreams', 'Street culture forever'],
    musicStyle: 'Hip-Hop Beats',
    gradient: 'from-orange-500 via-red-500 to-pink-500'
  },
  {
    id: 'night_life',
    name: 'Night Life',
    description: 'Clubs, parties, music, nocturnal energy',
    keywords: ['club lights', 'dj portrait', 'party lights', 'nightclub', 'concert crowd'],
    bioKeywords: ['nightlife lover', 'party enthusiast', 'music addict', 'nocturnal being', 'vibe curator'],
    videoKeywords: ['club party', 'dj music', 'party crowd', 'concert lights', 'nightclub dancing'],
    captionStyle: ['The night is ours 🌙', 'Lost in the music 🎵', 'Party never stops', 'Living for the drop', 'Nightlife energy ⚡'],
    musicStyle: 'Electronic Beats',
    gradient: 'from-fuchsia-500 via-purple-500 to-indigo-500'
  }
];

// ============================================
// PEXELS API HELPERS (Browser-safe fetch implementation)
// ============================================

/**
 * Fetch a photo from Pexels using browser-safe fetch()
 * Uses caching and fallbacks to handle rate limiting gracefully
 * @param {string} query - Search term
 * @param {string} orientation - 'portrait', 'landscape', or 'square'
 * @param {string} fallbackType - 'avatar' or 'post' for fallback selection
 * @returns {string} - Photo URL (never null - falls back to static images)
 */
async function fetchPexelsPhoto(query, orientation = 'portrait', fallbackType = 'post') {
  // Check cache first
  const cacheKey = `photo_${query}_${orientation}`;
  const cached = getCached(cacheKey);
  if (cached) {
    // Return random item from cached array
    return cached[Math.floor(Math.random() * cached.length)];
  }

  // If rate limited, skip API and use fallback
  if (isRateLimited && Date.now() < rateLimitResetTime) {
    return getRandomFallback(fallbackType);
  }

  if (!pexelsEnabled) {
    return getRandomFallback(fallbackType);
  }

  try {
    const params = new URLSearchParams({
      query,
      per_page: '20',
      orientation
    });

    const response = await fetch(`${PEXELS_PHOTO_API}/search?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': PEXELS_API_KEY,
        'Accept': 'application/json'
      }
    });

    // Handle rate limiting
    if (response.status === 429) {
      console.warn('Pexels API rate limited - using fallback images');
      isRateLimited = true;
      rateLimitResetTime = Date.now() + 60 * 60 * 1000; // Reset after 1 hour
      return getRandomFallback(fallbackType);
    }

    if (!response.ok) {
      throw new Error(`Pexels API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.photos && data.photos.length > 0) {
      // Cache all photo URLs for this query
      const photoUrls = data.photos.map(p => p.src.medium);
      setCache(cacheKey, photoUrls);

      const randomIndex = Math.floor(Math.random() * data.photos.length);
      return data.photos[randomIndex].src.medium;
    }
  } catch (error) {
    // Silently use fallback on errors
  }

  return getRandomFallback(fallbackType);
}

/**
 * Fetch a vertical video from Pexels using browser-safe fetch()
 * Uses caching and handles rate limiting - returns null on failure (images used as fallback)
 * @param {string} query - Search term
 * @returns {object|null} - Video data object or null
 */
async function fetchPexelsVideo(query) {
  // Check cache first
  const cacheKey = `video_${query}`;
  const cached = getCached(cacheKey);
  if (cached && cached.length > 0) {
    return cached[Math.floor(Math.random() * cached.length)];
  }

  // If rate limited, skip API
  if (isRateLimited && Date.now() < rateLimitResetTime) {
    return null;
  }

  if (!pexelsEnabled) {
    return null;
  }

  try {
    const params = new URLSearchParams({
      query,
      per_page: '15',
      orientation: 'portrait'
    });

    const response = await fetch(`${PEXELS_VIDEO_API}/search?${params}`, {
      method: 'GET',
      headers: {
        'Authorization': PEXELS_API_KEY,
        'Accept': 'application/json'
      }
    });

    // Handle rate limiting
    if (response.status === 429) {
      isRateLimited = true;
      rateLimitResetTime = Date.now() + 60 * 60 * 1000;
      return null;
    }

    if (!response.ok) {
      throw new Error(`Pexels API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.videos && data.videos.length > 0) {
      // Cache all video data for this query
      const videoData = data.videos.map(video => {
        const verticalFile = video.video_files.find(f => f.width < f.height) || video.video_files[0];
        return {
          url: verticalFile.link,
          thumbnail: video.image,
          width: verticalFile.width,
          height: verticalFile.height,
          duration: video.duration
        };
      });
      setCache(cacheKey, videoData);

      return videoData[Math.floor(Math.random() * videoData.length)];
    }
  } catch (error) {
    // Silently fail - images will be used as fallback
  }

  return null;
}

// ============================================
// CONTENT GENERATION HELPERS
// ============================================

/**
 * Generate a bio using persona keywords
 */
function generateBio(bioKeywords) {
  const templates = [
    `${faker.word.adjective()} ${bioKeywords[0]} | ${bioKeywords[1]}`,
    `${bioKeywords[2]} • ${faker.location.city()}`,
    `${bioKeywords[0]} × ${bioKeywords[1]} | Living ${bioKeywords[3]}`,
    `Professional ${bioKeywords[0]} | ${bioKeywords[4]} enthusiast`,
    `${faker.location.city()} based ${bioKeywords[1]} • ${bioKeywords[2]}`
  ];

  return templates[Math.floor(Math.random() * templates.length)];
}

/**
 * Generate a caption based on persona style
 */
function generateCaption(persona) {
  const caption = persona.captionStyle[Math.floor(Math.random() * persona.captionStyle.length)];
  const hashtags = [
    '#aesthetic',
    '#vibes',
    `#${persona.id}`,
    '#fyp',
    '#viral'
  ];

  // Randomly add 2-3 hashtags
  const selectedHashtags = hashtags
    .sort(() => 0.5 - Math.random())
    .slice(0, Math.floor(Math.random() * 2) + 2)
    .join(' ');

  return `${caption} ${selectedHashtags}`;
}

/**
 * Generate a music track name
 */
function generateMusicTrack(persona) {
  const artists = ['DJ NeonWave', 'SynthMaster', 'Digital Pulse', 'Urban Beats', 'Night Rider'];
  const artist = artists[Math.floor(Math.random() * artists.length)];
  return `${artist} - ${persona.musicStyle}`;
}

/**
 * Generate comment text
 */
function generateCommentText() {
  const comments = [
    'This is fire 🔥',
    'Absolutely insane!',
    'Need this energy in my life',
    'You never miss fr fr',
    'This is exactly what I needed today',
    'The vibes are immaculate ✨',
    'Bro this is legendary',
    'I\'m obsessed with this',
    'This hits different',
    'No cap this is amazing',
    'The aesthetic is perfect',
    'Living for this content',
    'You\'re too talented',
    'This is why you\'re the goat',
    'Main character energy',
    'I could watch this all day',
    'The way I ran here',
    'Bestie you ATE this up',
    'Periodt. 💯',
    'This is elite tier',
    'W content as always',
    'You understood the assignment',
    'This is art',
    'Manifesting this energy',
    'The talent is unreal'
  ];

  return comments[Math.floor(Math.random() * comments.length)];
}

// ============================================
// USER GENERATION
// ============================================

/**
 * Generate 25 Ghost Users with Pexels profile pictures
 */
async function generateGhostUsers() {
  console.log('🎭 Generating 25 Ghost Users with Personas...');
  const users = [];
  const usedUsernames = new Set();

  for (let i = 0; i < 25; i++) {
    // Randomly assign a persona
    const persona = PERSONAS[Math.floor(Math.random() * PERSONAS.length)];

    // Get unique username
    let username;
    do {
      username = faker.internet.username().replace(/[^a-zA-Z0-9_]/g, '_');
    } while (usedUsernames.has(username));
    usedUsernames.add(username);

    // Fetch profile picture from Pexels using persona keywords
    const photoQuery = persona.keywords[Math.floor(Math.random() * persona.keywords.length)];
    let avatar_url = null;

    if (pexelsEnabled) {
      avatar_url = await fetchPexelsPhoto(photoQuery);
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    users.push({
      id: `ghost-${i + 1}`,
      username,
      avatar_url,
      persona: persona.id,
      bio: generateBio(persona.bioKeywords),
      gradient: persona.gradient,
      status: ['online', 'online', 'away', 'offline'][Math.floor(Math.random() * 4)] // 50% online
    });

    // Log progress
    if ((i + 1) % 5 === 0) {
      console.log(`  ✓ Generated ${i + 1}/25 users`);
    }
  }

  console.log('✅ All 25 Ghost Users created!');
  return users;
}

// ============================================
// FEED GENERATION
// ============================================

/**
 * Generate feed posts with Pexels videos
 * @param {number} count - Number of posts to generate
 * @param {array} users - Array of ghost users
 */
async function generateFeed(users, count = 60) {
  console.log(`📹 Generating ${count} Feed Posts with Pexels Videos...`);
  const feed = [];
  const now = Date.now();

  for (let i = 0; i < count; i++) {
    // Pick random author
    const author = users[Math.floor(Math.random() * users.length)];
    const persona = PERSONAS.find(p => p.id === author.persona);

    // Fetch video matching the author's persona
    const videoQuery = persona.videoKeywords[Math.floor(Math.random() * persona.videoKeywords.length)];
    let videoData = null;

    if (pexelsEnabled) {
      videoData = await fetchPexelsVideo(videoQuery);
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    // Generate realistic engagement
    const baseEngagement = Math.floor(Math.random() * 40000) + 500;
    const likes = baseEngagement;
    const comments = Math.floor(likes * (Math.random() * 0.05 + 0.02)); // 2-7% of likes
    const shares = Math.floor(likes * (Math.random() * 0.02 + 0.005)); // 0.5-2.5% of likes

    // Smart timestamps (last 2 hours)
    const minutesAgo = Math.floor(Math.random() * 120) + 1;
    const timestamp = new Date(now - minutesAgo * 60 * 1000);

    feed.push({
      id: `feed-${i + 1}`,
      username: author.username,
      userId: author.id,
      avatar: author.avatar_url,
      videoUrl: videoData?.url || null,
      thumbnail: videoData?.thumbnail || null,
      caption: generateCaption(persona),
      likes,
      comments,
      shares,
      music: generateMusicTrack(persona),
      gradient: author.gradient,
      timestamp,
      minutesAgo,
      persona: persona.id
    });

    // Log progress
    if ((i + 1) % 10 === 0) {
      console.log(`  ✓ Generated ${i + 1}/${count} posts`);
    }
  }

  console.log(`✅ All ${count} Feed Posts created!`);
  return feed.sort((a, b) => b.timestamp - a.timestamp);
}

// ============================================
// COMMENT GENERATION
// ============================================

/**
 * Generate 3-8 comments per post
 */
function generateComments(feed, users) {
  console.log('💬 Generating Comments...');

  const feedWithComments = feed.map(post => {
    const commentCount = Math.floor(Math.random() * 6) + 3; // 3-8 comments
    const comments = [];

    // Get random commenters (excluding post author)
    const potentialCommenters = users.filter(u => u.id !== post.userId);

    for (let i = 0; i < commentCount; i++) {
      const commenter = potentialCommenters[Math.floor(Math.random() * potentialCommenters.length)];

      // Comments come after the post
      const minutesAfterPost = Math.floor(Math.random() * 60) + 1;
      const commentTimestamp = new Date(post.timestamp.getTime() + minutesAfterPost * 60 * 1000);

      comments.push({
        id: `comment-${post.id}-${i}`,
        userId: commenter.id,
        username: commenter.username,
        avatar: commenter.avatar_url,
        gradient: commenter.gradient,
        text: generateCommentText(),
        timestamp: commentTimestamp,
        likes: Math.floor(Math.random() * 100)
      });
    }

    return {
      ...post,
      commentList: comments.sort((a, b) => a.timestamp - b.timestamp)
    };
  });

  console.log('✅ Comments generated!');
  return feedWithComments;
}

// ============================================
// CHATS & SPACES GENERATION (Enhanced)
// ============================================

/**
 * Generate chats using Ghost Users
 */
function generateChats(users) {
  const chatCount = Math.floor(Math.random() * 3) + 8; // 8-10 chats
  const selectedUsers = users
    .filter(u => u.status === 'online' || u.status === 'away')
    .sort(() => 0.5 - Math.random())
    .slice(0, chatCount);

  const now = Date.now();

  const chatMessages = [
    "Bro did you see that new post?",
    "Your latest drop was insane 🔥",
    "Wanna hop in a call?",
    "Just finished editing this, thoughts?",
    "The algorithm blessed me today",
    "No cap your content is elite",
    "That setup is absolutely chef's kiss",
    "Catch me online later?",
    "This new update hits different fr",
    "Sending you the files now ✨"
  ];

  return selectedUsers.map((user, index) => {
    let minutesAgo;
    if (index < 2) {
      minutesAgo = Math.floor(Math.random() * 10) + 1; // Very recent
    } else if (index < 5) {
      minutesAgo = Math.floor(Math.random() * 60) + 10; // Recent
    } else {
      minutesAgo = Math.floor(Math.random() * 300) + 60; // 1-6 hours
    }

    const lastMessageTime = new Date(now - minutesAgo * 60 * 1000);
    const unread = Math.random() < 0.3 ? Math.floor(Math.random() * 5) + 1 : 0;

    return {
      id: user.id,
      username: user.username,
      avatar_url: user.avatar_url,
      gradient: user.gradient,
      status: index === 1 && user.status === 'online' ? 'typing' : user.status,
      lastMessage: chatMessages[Math.floor(Math.random() * chatMessages.length)],
      lastMessageTime,
      unread
    };
  }).sort((a, b) => b.lastMessageTime - a.lastMessageTime);
}

/**
 * Generate chat history for a specific user
 */
function getChatHistory(chatId, users) {
  const messageCount = Math.floor(Math.random() * 15) + 5;
  const messages = [];
  const now = Date.now();

  const chatMessages = [
    "Hey! What's good?",
    "Did you catch the latest drop?",
    "This new feature is wild",
    "Absolutely loving the vibes rn",
    "Wanna collab on something?",
    "The content you're putting out is 🔥",
    "Just finished editing my latest project",
    "Thoughts on this idea?",
    "We should definitely link up soon",
    "That setup reveal was insane"
  ];

  for (let i = 0; i < messageCount; i++) {
    const minutesAgo = (messageCount - i) * (Math.floor(Math.random() * 10) + 5);
    const timestamp = new Date(now - minutesAgo * 60 * 1000);
    const sender = i % 2 === 0 ? 'them' : 'me';

    messages.push({
      id: `msg-${chatId}-${i}`,
      sender,
      text: chatMessages[Math.floor(Math.random() * chatMessages.length)],
      timestamp
    });
  }

  return messages;
}

/**
 * Generate spaces
 */
function generateSpaces() {
  const spaces = [
    { name: 'Neon Lounge', description: 'Cyberpunk vibes and chill talks', emoji: '💜' },
    { name: 'Code Dungeon', description: 'Where developers never sleep', emoji: '💻' },
    { name: 'Gaming Arena', description: 'GG EZ - Play together, win together', emoji: '🎮' },
    { name: 'Art Collective', description: 'Digital creativity unleashed', emoji: '🎨' },
    { name: 'Music Factory', description: 'Sharing beats and discovering fire', emoji: '🎵' },
    { name: 'Street Culture', description: 'Urban fashion and street art', emoji: '🏙️' },
    { name: 'Night Owls', description: '3AM thoughts hit different', emoji: '🦉' },
    { name: 'Tech Hub', description: 'Innovation and cutting edge tech', emoji: '⚡' }
  ];

  return spaces.map((space, index) => ({
    id: `space-${index + 1}`,
    name: space.name,
    description: space.description,
    emoji: space.emoji,
    icon_url: null,
    unreadMessages: Math.random() < 0.5 ? Math.floor(Math.random() * 30) : 0,
    unreadMentions: Math.random() < 0.2 ? Math.floor(Math.random() * 5) + 1 : 0,
    memberCount: Math.floor(Math.random() * 5000) + 50
  }));
}

/**
 * Generate notifications
 */
function generateNotifications(users) {
  const notificationCount = Math.floor(Math.random() * 15) + 10;
  const notifications = [];
  const now = Date.now();

  const types = ['like', 'comment', 'follow', 'mention'];
  const templates = {
    like: (user) => `${user.username} liked your post`,
    comment: (user) => `${user.username} commented: "${generateCommentText()}"`,
    follow: (user) => `${user.username} started following you`,
    mention: (user) => `${user.username} mentioned you in a post`
  };

  for (let i = 0; i < notificationCount; i++) {
    const user = users[Math.floor(Math.random() * users.length)];
    const type = types[Math.floor(Math.random() * types.length)];
    const minutesAgo = Math.floor(Math.random() * 1440); // Last 24 hours
    const timestamp = new Date(now - minutesAgo * 60 * 1000);
    const isRead = Math.random() < 0.6;

    notifications.push({
      id: `notif-${i}`,
      userId: user.id,
      username: user.username,
      avatar: user.avatar_url,
      gradient: user.gradient,
      type,
      message: templates[type](user),
      timestamp,
      isRead
    });
  }

  return notifications.sort((a, b) => b.timestamp - a.timestamp);
}

// ============================================
// MAIN INITIALIZATION FUNCTION
// ============================================

/**
 * Initialize all Ghost Data with Pexels integration
 * This is the main function to call from your app
 */
export async function initializeGhostData() {
  console.log('🚀 Initializing Advanced Ghost Data...');
  console.log(pexelsEnabled ? '📸 Pexels API enabled' : '📸 Pexels API disabled (no API key)');
  console.log('🎬 Using Backstage Buffer Strategy: 60 live posts + 30 buffer posts');

  try {
    // Step 1: Generate 25 users with Pexels profile pictures
    const users = await generateGhostUsers();

    // Step 2: Generate 90 feed posts (60 live + 30 buffer)
    let allPosts = await generateFeed(users, 90);

    // Step 3: Split into live feed and contentBuffer
    const feed = allPosts.slice(0, 60);
    const contentBuffer = allPosts.slice(60, 90);

    // Step 4: Add comments only to live feed posts
    const feedWithComments = generateComments(feed, users);

    // Step 5: Generate other data
    const chats = generateChats(users);
    const spaces = generateSpaces();
    const notifications = generateNotifications(users);

    console.log('✅ Ghost Data Initialization Complete!');
    console.log(`  - ${users.length} users created`);
    console.log(`  - ${feedWithComments.length} live posts`);
    console.log(`  - ${contentBuffer.length} buffer posts (ready for simulation)`);
    console.log(`  - ${chats.length} active chats`);
    console.log(`  - ${spaces.length} spaces`);
    console.log(`  - ${notifications.length} notifications`);

    return {
      users,
      feed: feedWithComments,
      contentBuffer, // 🎬 NEW: Buffer for simulation
      chats,
      spaces,
      notifications,
      currentUser: {
        id: 'current-user',
        username: 'Me',
        avatar_url: null,
        gradient: 'from-gjyl-accent via-purple-500 to-pink-500',
        status: 'online'
      }
    };
  } catch (error) {
    console.error('❌ Error initializing Ghost Data:', error);
    // Return fallback data if Pexels fails
    return getFallbackData();
  }
}

// ============================================
// FALLBACK DATA (If Pexels API fails)
// ============================================

/**
 * Fallback to basic data if Pexels is unavailable
 */
function getFallbackData() {
  console.warn('⚠️ Using fallback data (Pexels unavailable)');

  // Import the original DataSeeder as fallback
  // For now, return minimal structure
  return {
    users: [],
    feed: [],
    contentBuffer: [], // Include buffer in fallback
    chats: [],
    spaces: [],
    notifications: [],
    currentUser: {
      id: 'current-user',
      username: 'Me',
      avatar_url: null,
      gradient: 'from-gjyl-accent via-purple-500 to-pink-500',
      status: 'online'
    }
  };
}

// ============================================
// UTILITY EXPORTS
// ============================================

export {
  PERSONAS,
  generateGhostUsers,
  generateFeed,
  generateComments,
  generateChats,
  getChatHistory,
  generateSpaces,
  generateNotifications
};

// Default export
export default {
  initializeGhostData,
  PERSONAS
};
